import { AdminOnly } from '@/components/admin-only'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ArrowUpRight, Sparkles } from 'lucide-react'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase'
import { useQuotes } from '@/hooks/use-quotes'
import { useClients, clientName } from '@/hooks/use-clients'
import { money } from '@/lib/format'
import { Page, PageHeader, Card, Table, Trow, StatusPill, TabPills, Pagination, SearchInput, SkeletonRows, EmptyState, Workspace, btnPrimary, btnSecondary } from '@/components/rm'
import { Modal, Field, Input, Select } from '@/components/rm/form'
import { phFormat } from '@/lib/ph'

export const Route = createFileRoute('/_app/quotes')({ component: () => <AdminOnly><QuotesPage /></AdminOnly> })

const COLS = '90px 1.4fr 1.4fr 120px 120px 110px'
const PAGE = 15
// PostgREST or() filters are comma/paren delimited, so a search containing those
// characters would corrupt the filter string. Strip them rather than escape.
const safe = (s: string) => s.replace(/[,()]/g, ' ').trim()
const ago = (iso: string | null) => {
  if (!iso) return '—'
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)
  if (d <= 0) return 'today'
  if (d === 1) return '1 day ago'
  if (d < 7) return `${d} days ago`
  if (d < 14) return '1 week ago'
  if (d < 60) return `${Math.floor(d / 7)} weeks ago`
  return `${Math.floor(d / 30)} mo ago`
}

interface QuoteRow {
  id: number
  quote_number: string | null
  title: string | null
  status: string
  total: number
  currency: string | null
  sent_at: string | null
  client: { id: string; first_name: string | null; last_name: string | null; organization: string | null } | null
}

interface QuotePreviewItem {
  id: number
  position: number | null
  description: string | null
  quantity: number | null
  unit_price: number | null
  amount: number | null
  optional: boolean | null
}

interface QuotePreview {
  id: number
  quote_number: string | null
  title: string | null
  status: string
  notes: string | null
  currency: string | null
  subtotal: number | null
  tax: number | null
  total: number | null
  deposit_amount: number | null
  valid_until: string | null
  sent_at: string | null
  accepted_at: string | null
  created_at: string | null
  client: { id: string; first_name: string | null; last_name: string | null; organization: string | null; email: string | null; phone: string | null } | null
  items: QuotePreviewItem[] | null
}

const fmtDay = (iso: string | null) => {
  if (!iso) return '—'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return '—'
  return phFormat(d, 'date', { day: 'numeric', month: 'short', year: 'numeric' })
}

/** The right-hand pane: read the quote without leaving the list. Editing lives in the builder. */
function QuoteWorkspacePreview({ quote, isLoading, isError, hasSelection, onRetry, onOpen }: {
  quote: QuotePreview | undefined
  isLoading: boolean
  isError: boolean
  hasSelection: boolean
  onRetry: () => void
  onOpen: () => void
}) {
  if (!hasSelection) {
    return (
      <div className="flex h-full flex-col items-center justify-center px-6 py-16 text-center">
        <span className="mb-4 flex h-11 w-11 items-center justify-center rounded-[12px] bg-token-selected text-brand"><ArrowUpRight className="h-5 w-5" /></span>
        <p className="text-[14px] font-semibold text-token-primary">Select a quote</p>
        <p className="mt-1 max-w-[280px] text-[13px] text-token-secondary">Choose a quote from the list to read it without losing your filters.</p>
      </div>
    )
  }
  if (isLoading) {
    return <div className="px-6 py-5" role="status" aria-live="polite" aria-label="Loading selected quote"><SkeletonRows count={7} /></div>
  }
  if (isError || !quote) {
    return (
      <div className="flex h-full flex-col items-center justify-center px-6 py-16 text-center">
        <p className="text-[14px] font-semibold text-token-primary">Couldn't load this quote</p>
        <button className={btnSecondary + ' mt-3'} onClick={onRetry}>Try again</button>
      </div>
    )
  }

  const items = [...(quote.items ?? [])].sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
  const cur = quote.currency

  return (
    <div className="flex h-full flex-col">
      {/* No wrapping: a long quote title used to shove the action onto its own line. The title
          truncates instead, which keeps the button in the same place on every quote. */}
      <header className="flex items-start justify-between gap-4 border-b border-token-subtle px-6 py-4">
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-token-muted">{quote.quote_number ?? 'Quote'}</p>
          <div className="mt-1 flex items-center gap-2.5">
            <h2 className="truncate font-display text-[19px] font-semibold text-token-primary" title={quote.title ?? undefined}>{quote.title || 'Untitled quote'}</h2>
            <span className="flex-none"><StatusPill status={quote.status} /></span>
          </div>
        </div>
        <button className={btnPrimary + ' flex-none'} onClick={onOpen}>Open quote <ArrowUpRight className="h-4 w-4" /></button>
      </header>

      <dl className="grid grid-cols-2 gap-px border-b border-token-subtle bg-token-subtle min-[1400px]:grid-cols-4">
        {[
          { label: 'Client', value: quote.client ? clientName(quote.client) : 'No client' },
          { label: 'Created', value: fmtDay(quote.created_at) },
          { label: 'Sent', value: quote.sent_at ? fmtDay(quote.sent_at) : 'Not sent' },
          { label: 'Valid until', value: quote.valid_until ? fmtDay(quote.valid_until) : '—' },
        ].map((cell) => (
          <div key={cell.label} className="bg-token-surface px-5 py-3.5">
            <dt className="text-[11px] font-semibold uppercase tracking-[0.05em] text-token-muted">{cell.label}</dt>
            <dd className="mt-1 truncate text-[14px] font-medium text-token-primary">{cell.value}</dd>
          </div>
        ))}
      </dl>

      <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
        <h3 className="mb-3 text-[13px] font-semibold text-token-primary">Line items</h3>
        {items.length === 0 ? (
          <p className="text-[13px] text-token-secondary">No line items on this quote yet.</p>
        ) : (
          <ul className="divide-y divide-token-subtle rounded-[12px] border border-token-subtle">
            {items.map((item) => (
              <li key={item.id} className="flex items-start justify-between gap-4 px-4 py-3">
                <span className="min-w-0">
                  <span className="block text-[13.5px] text-token-primary">{item.description || 'Untitled line'}</span>
                  <span className="mt-0.5 block text-[12px] text-token-secondary">
                    {Number(item.quantity ?? 0)} × {money(Number(item.unit_price ?? 0), cur)}
                    {item.optional ? ' · Optional' : ''}
                  </span>
                </span>
                <span className="whitespace-nowrap font-display text-[13.5px] font-semibold tabular-nums text-token-primary">{money(Number(item.amount ?? 0), cur)}</span>
              </li>
            ))}
          </ul>
        )}

        <dl className="mt-4 ml-auto w-full max-w-[280px] space-y-1.5 text-[13.5px]">
          <div className="flex justify-between"><dt className="text-token-secondary">Subtotal</dt><dd className="tabular-nums text-token-primary">{money(Number(quote.subtotal ?? 0), cur)}</dd></div>
          <div className="flex justify-between"><dt className="text-token-secondary">Tax</dt><dd className="tabular-nums text-token-primary">{money(Number(quote.tax ?? 0), cur)}</dd></div>
          <div className="flex justify-between border-t border-token-subtle pt-1.5 font-semibold"><dt className="text-token-primary">Total</dt><dd className="font-display tabular-nums text-token-primary">{money(Number(quote.total ?? 0), cur)}</dd></div>
          {Number(quote.deposit_amount ?? 0) > 0 && (
            <div className="flex justify-between"><dt className="text-token-secondary">Deposit</dt><dd className="tabular-nums text-token-primary">{money(Number(quote.deposit_amount), cur)}</dd></div>
          )}
        </dl>

        {quote.notes && (
          <>
            <h3 className="mb-2 mt-6 text-[13px] font-semibold text-token-primary">Notes</h3>
            <p className="whitespace-pre-wrap text-[13.5px] leading-relaxed text-token-secondary">{quote.notes}</p>
          </>
        )}
      </div>
    </div>
  )
}

function QuotesPage() {
  const navigate = useNavigate()
  const { create } = useQuotes()
  const { clients } = useClients()
  const [filter, setFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [open, setOpen] = useState(false)
  const [title, setTitle] = useState('')
  const [clientId, setClientId] = useState('')
  const reset = () => setPage(0)

  // Tab counts — cheap head queries, independent of the current page.
  const counts = useQuery({
    queryKey: ['quotes', 'counts'],
    queryFn: async () => {
      const head = { count: 'exact' as const, head: true }
      const base = () => supabase.from('quote').select('id', head).is('deleted_at', null)
      const [all, draft, sent, accepted, declined] = await Promise.all([
        base(),
        base().eq('status', 'draft'),
        base().in('status', ['sent', 'viewed']),
        base().eq('status', 'accepted'),
        base().eq('status', 'declined'),
      ])
      return {
        all: all.count ?? 0,
        draft: draft.count ?? 0,
        sent: sent.count ?? 0,
        accepted: accepted.count ?? 0,
        declined: declined.count ?? 0,
      }
    },
  })

  // Server-side filtered + paginated page (mirrors the jobs list).
  const quotesPage = useQuery({
    queryKey: ['quotes', 'page', { filter, search, page }],
    queryFn: async () => {
      let q = supabase
        .from('quote')
        .select('id, quote_number, title, status, total, currency, sent_at, client:contact(id, first_name, last_name, organization)', { count: 'exact' })
        .is('deleted_at', null)
      if (filter === 'sent') q = q.in('status', ['sent', 'viewed'])
      else if (filter !== 'all') q = q.eq('status', filter)

      const s = safe(search)
      if (s) {
        // Client names live on the joined contact row, which an or() on `quote` can't
        // reach. Resolve matching client ids from the already-loaded list and fold them
        // into the same filter so search-by-client-name keeps working.
        const ids = clients.filter((c) => clientName(c).toLowerCase().includes(s.toLowerCase())).map((c) => c.id)
        const parts = [`quote_number.ilike.%${s}%`, `title.ilike.%${s}%`]
        if (ids.length) parts.push(`contact_id.in.(${ids.join(',')})`)
        q = q.or(parts.join(','))
      }

      const { data, error, count } = await q
        .order('created_at', { ascending: false })
        .range(page * PAGE, page * PAGE + PAGE - 1)
      if (error) throw error
      return { rows: (data ?? []) as unknown as QuoteRow[], count: count ?? 0 }
    },
  })

  const rows = quotesPage.data?.rows ?? []
  const c = counts.data

  /**
   * Selection is LOCAL, and deliberately does not change the URL the way the Jobs rail does.
   * /quotes/:id is the editable builder, not a record view. Driving it from the rail would mean
   * clicking between quotes while a document has unsaved edits, which either fights the unsaved
   * guard on every row or risks losing work. Keeping the selection here gives the same two-column
   * reading view with none of that, and "Open quote" is the way into the builder.
   *
   * The cost is that a refresh forgets the selection, which the preselect below then repairs.
   */
  const [selectedId, setSelectedId] = useState<number | null>(null)

  // Preselect the first quote so the pane opens with something to read, on the wide layout only
  // (the rail starts at 1200px; below it the table is shown) and never over an existing choice.
  const firstRowId = rows[0]?.id ?? null
  useEffect(() => {
    if (selectedId !== null || firstRowId === null) return
    if (!window.matchMedia('(min-width: 1200px)').matches) return
    setSelectedId(firstRowId)
  }, [selectedId, firstRowId])

  // Changing tab, search or page can drop the selected quote out of the list. Fall back to the
  // first row of the new list rather than leaving the pane pointing at something no longer shown.
  useEffect(() => {
    if (selectedId === null || rows.length === 0) return
    if (!rows.some((q) => q.id === selectedId)) setSelectedId(rows[0].id)
  }, [rows, selectedId])

  const preview = useQuery({
    queryKey: ['quote-preview', selectedId],
    enabled: selectedId !== null,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('quote')
        .select('id, quote_number, title, status, notes, currency, subtotal, tax, total, deposit_amount, valid_until, sent_at, accepted_at, created_at, client:contact(id, first_name, last_name, organization, email, phone), items:quote_item(id, position, description, quantity, unit_price, amount, optional)')
        .eq('id', selectedId!)
        .single()
      if (error) throw error
      return data as unknown as QuotePreview
    },
  })

  const submit = async () => {
    if (!title.trim()) return toast.error('Enter a quote title.')
    try {
      const q = await create.mutateAsync({ title: title.trim(), client_id: clientId || null })
      setTitle(''); setClientId(''); setOpen(false)
      navigate({ to: '/quotes/$id', params: { id: String(q.id) } })
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Failed') }
  }

  return (
    <Page>
      <PageHeader
        title="Quotes"
        subtitle={`${quotesPage.data?.count ?? 0} quotes in this view`}
        actions={<button className={btnPrimary} onClick={() => setOpen(true)}><Sparkles className="h-4 w-4" /> New AI quote</button>}
      />
      <div className="mt-5 mb-[18px] flex flex-wrap items-center justify-between gap-3">
        <TabPills active={filter} onChange={(v) => { setFilter(v); reset() }} chips={[
          { id: 'all', label: 'All', count: c?.all },
          { id: 'draft', label: 'Draft', count: c?.draft },
          { id: 'sent', label: 'Sent', count: c?.sent },
          { id: 'accepted', label: 'Accepted', count: c?.accepted },
          { id: 'declined', label: 'Declined', count: c?.declined },
        ]} />
        <div className="flex flex-1 items-center justify-end gap-2">
          <SearchInput value={search} onChange={(v) => { setSearch(v); reset() }} placeholder="Search quotes…" />
        </div>
      </div>

      {/* Wide: the rail + preview workspace, same shell the Jobs page uses. */}
      <div className="hidden min-[1200px]:block">
        <Workspace
          list={
            <div className="flex h-full flex-col">
              <div className="border-b border-token-subtle px-4 py-3">
                <p className="text-[13px] font-semibold text-token-primary">Quotes</p>
                <p className="mt-0.5 text-[12px] text-token-secondary">{quotesPage.data?.count ?? 0} matching this view</p>
              </div>
              {quotesPage.isLoading ? (
                <SkeletonRows count={9} />
              ) : rows.length === 0 ? (
                <EmptyState label={(c?.all ?? 0) === 0 ? 'No quotes yet.' : 'No quotes match these filters.'} cta={(c?.all ?? 0) === 0 ? 'New quote' : undefined} onCta={() => setOpen(true)} />
              ) : (
                <div className="divide-y divide-token-subtle">
                  {rows.map((q) => {
                    const selected = q.id === selectedId
                    return (
                      <button
                        key={q.id}
                        onClick={() => setSelectedId(q.id)}
                        aria-current={selected ? 'page' : undefined}
                        className={`relative flex w-full items-center gap-3 px-4 py-3 text-left transition-colors ${selected ? 'bg-token-selected' : 'hover:bg-token-hover'}`}
                      >
                        {selected && <span className="absolute inset-y-0 left-0 w-[3px] bg-brand" />}
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-[13.5px] font-semibold text-token-primary">{q.title || 'Untitled quote'}</span>
                          <span className="mt-0.5 block truncate text-[12px] text-token-secondary">{q.quote_number ?? '—'} · {q.client ? clientName(q.client) : 'No client'}</span>
                          <span className="mt-1 block truncate text-[11px] text-token-muted">{money(q.total, q.currency, { whole: true })} · {q.sent_at ? `Sent ${ago(q.sent_at)}` : 'Not sent'}</span>
                        </span>
                        <StatusPill status={q.status} />
                      </button>
                    )
                  })}
                </div>
              )}
              <div className="mt-auto border-t border-token-subtle">
                <Pagination page={page} pageSize={PAGE} total={quotesPage.data?.count ?? 0} onPage={(next) => { setSelectedId(null); setPage(next) }} />
              </div>
            </div>
          }
          inspector={
            <QuoteWorkspacePreview
              quote={preview.data}
              isLoading={preview.isLoading}
              isError={preview.isError}
              hasSelection={selectedId !== null}
              onRetry={() => preview.refetch()}
              onOpen={() => selectedId !== null && navigate({ to: '/quotes/$id', params: { id: String(selectedId) } })}
            />
          }
        />
      </div>

      {/* Narrow: the original table, unchanged. */}
      <div className="min-[1200px]:hidden">
      {quotesPage.isLoading ? (
        <Card><SkeletonRows count={5} /></Card>
      ) : rows.length === 0 ? (
        <Card><EmptyState label={(c?.all ?? 0) === 0 ? 'No quotes yet.' : 'No quotes match these filters.'} cta={(c?.all ?? 0) === 0 ? 'New quote' : undefined} onCta={() => setOpen(true)} /></Card>
      ) : (
        <Table cols={COLS} header={<><span>Quote</span><span>Client</span><span>Job</span><span className="text-right">Amount · inc VAT</span><span>Sent</span><span className="text-right">Status</span></>}>
          {rows.map((q, i) => (
            <Trow key={q.id} cols={COLS} last={i === rows.length - 1} onClick={() => navigate({ to: '/quotes/$id', params: { id: String(q.id) } })}>
              <span className="font-display font-bold text-token-primary">{q.quote_number}</span>
              <span className="truncate font-bold text-token-primary">{q.client ? clientName(q.client) : '—'}</span>
              <span className="truncate text-token-secondary">{q.title || 'Untitled quote'}</span>
              <span className="font-display text-right font-bold tabular-nums text-token-primary">{money(q.total, q.currency, { whole: true })}</span>
              <span className="text-token-secondary">{q.sent_at ? ago(q.sent_at) : '—'}</span>
              <span className="flex justify-end"><StatusPill status={q.status} /></span>
            </Trow>
          ))}
          <Pagination page={page} pageSize={PAGE} total={quotesPage.data?.count ?? 0} onPage={setPage} />
        </Table>
      )}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="New quote"
        footer={<><button className={btnSecondary} onClick={() => setOpen(false)}>Cancel</button><button className={btnPrimary} onClick={submit} disabled={create.isPending}>{create.isPending ? 'Creating…' : 'Create & edit'}</button></>}>
        <div className="space-y-4">
          <Field label="Title"><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Switchboard upgrade" /></Field>
          <Field label="Client">
            <Select value={clientId} onChange={(e) => setClientId(e.target.value)}>
              <option value="">No client</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{clientName(c)}</option>)}
            </Select>
          </Field>
        </div>
      </Modal>
    </Page>
  )
}
